import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { CountryCode, getCountries } from 'libphonenumber-js';

/**
 * Pays sélectionnable pour l'adresse postale du conseiller.
 * `name` est le libellé localisé (fourni par `Intl.DisplayNames`), pas une clé i18n.
 */
export type ProfileCountry = {
    iso2: CountryCode,
    name: string,
};

/** Marchés EnvestBoard courants, épinglés en tête de liste (dans cet ordre). */
const PRIORITY_COUNTRIES: readonly CountryCode[] = [
    'FR', 'BE', 'LU', 'CH', 'DE', 'ES', 'IT', 'PT', 'MC', 'GB', 'US', 'CA',
];

/**
 * Motifs de validation du FORMAT de code postal, par pays.
 * Les pays absents de cette table ne sont soumis à AUCUNE contrainte de format
 * (on ne bloque pas arbitrairement les ~250 pays de la liste).
 */
const POSTAL_CODE_PATTERNS: Partial<Record<CountryCode, RegExp>> = {
    FR: /^\d{5}$/,
    BE: /^\d{4}$/,
    LU: /^\d{4}$/,
    CH: /^\d{4}$/,
    DE: /^\d{5}$/,
    ES: /^\d{5}$/,
    IT: /^\d{5}$/,
    PT: /^\d{4}-\d{3}$/,
    MC: /^980\d{2}$/,
    GB: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i,
    US: /^\d{5}(-\d{4})?$/,
    CA: /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/i,
};

/**
 * Construit la liste complète des pays (tous ceux connus de `libphonenumber-js`),
 * libellés dans la langue passée, marchés prioritaires en tête puis le reste trié
 * alphabétiquement selon la locale.
 */
export function buildProfileCountries(locale: string): ProfileCountry[] {
    const display = new Intl.DisplayNames([locale], { type: 'region' });
    const named: ProfileCountry[] = getCountries()
        .map((iso2) => ({ iso2, name: display.of(iso2) ?? iso2 }));

    const priority = PRIORITY_COUNTRIES
        .map((iso2) => named.find((country) => country.iso2 === iso2))
        .filter((country): country is ProfileCountry => country !== undefined);

    const rest = named
        .filter((country) => !PRIORITY_COUNTRIES.includes(country.iso2))
        .sort((a, b) => a.name.localeCompare(b.name, locale));

    return [...priority, ...rest];
}

/**
 * Valide le code postal selon le pays sélectionné.
 *
 * Décisions :
 * - champ vide → on laisse passer (la présence est portée par un éventuel `Validators.required`,
 *   pas par ce validateur de format) ;
 * - pays sans motif connu → on n'impose AUCUN format (pas de blocage arbitraire) ;
 * - sinon → erreur `{ invalid_postal_code: { country } }` si le motif ne matche pas.
 */
export function postalCodeValidatorForCountry(country: CountryCode | null | undefined): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = (control.value as string | null)?.trim();
        if (!value) {
            return null;
        }
        const pattern = country ? POSTAL_CODE_PATTERNS[country] : undefined;
        if (!pattern) {
            return null;
        }
        return pattern.test(value) ? null : { invalid_postal_code: { country } };
    };
}
